import React, { useEffect, useRef, useState } from 'react';
import styles from '../../Components/RightSidebar/rightsidebar.module.css';
import BookingDetails from '../../Components/BookingDetails/BookingDetails';
import { getInitials, getConsistentColor } from '../../Utils/utils';
import PrimiumMemberIcon from '../../Components/Icons/PrimiumMemberIcon';
import { SaleNote } from '../../Components/SaleNote/SaleNote';
import ms from './MembershipSaleSidebarInfo.module.css';
import menuIcon from '../../Assets/images/menu.svg';
import { SaleCancelReason } from '../../Components/SaleCancelReason/SaleCancelReason';

const formatMoney = (n) => {
  const v = parseFloat(n);
  if (Number.isNaN(v)) return '0.00';
  const abs = Math.abs(v);
  const str = abs.toFixed(2);
  if (v < 0) return `-${str}`;
  return str;
};

/** Headline amount: whole dollars without ".00" when exact (e.g. 500). */
const formatHeadlineAmount = (n) => {
  const v = parseFloat(n);
  if (Number.isNaN(v)) return '0';
  const abs = Math.abs(v);
  if (Math.abs(abs - Math.round(abs)) < 1e-9) {
    return v < 0 ? `-${Math.round(abs)}` : `${Math.round(abs)}`;
  }
  const str = abs.toFixed(2);
  return v < 0 ? `-${str}` : str;
};

const displayPhone = (phone) => {
  const s = String(phone || '').trim();
  if (!s) return '—';
  return s.replace(/^\+/, '');
};

const giftLabelText = (g) => (g.gift_name || g.remarks || '').trim();

const hasAssignedGift = (g) => {
  const id = g?.gift_id;
  return id != null && id !== '';
};

const getGiftReturnStatus = (gift) => {
  const explicit = String(gift?.return_status || gift?.gift_status || '').trim().toLowerCase();
  const remarks = String(gift?.remarks || '').toLowerCase();
  const match = remarks.match(/return_status=([a-z_ -]+)/i);
  const raw = explicit || (match ? match[1].trim().toLowerCase() : '');

  if (raw === 'returned') return 'Returned';
  if (raw === 'unreturned' || raw === 'not_returned' || raw === 'not returned') return 'Not Returned';
  if (raw === 'did_not_issue' || raw === 'did not issue') return 'Did not issue';
  return '';
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const getPaymentMethodName = (payment) =>
  payment?.payment_method_name || payment?.payment_method || '';

const getPaymentAmountValue = (payment) =>
  payment?.amount_value ?? payment?.amount ?? payment?.refund_amount ?? 0;

const hasPaymentMethod = (payment) => Boolean(getPaymentMethodName(payment));

const isRefundPaymentRowByCode = (payment) =>
  String(payment?.result_code ?? '').trim().toLowerCase() === 'refund requested';

const isRefundPaymentRow = (payment) =>
  isRefundPaymentRowByCode(payment) || Boolean(payment?.__isRefundRow);

const getPaymentTransactionId = (payment) =>
  payment?.transaction_id ?? payment?.id ?? payment?.payment_transaction_id ?? payment?.payment_id ?? null;

const getParentTransactionId = (payment) => {
  const parentId = payment?.parent_transaction_id ?? payment?.parent_payment_transaction_id ?? null;
  if (parentId == null || String(parentId).trim() === '') return null;
  return String(parentId);
};

const comparePaymentsNewestFirst = (a, b) => {
  const aCreatedAt = Date.parse(a.payment?.created_at || '');
  const bCreatedAt = Date.parse(b.payment?.created_at || '');
  if (Number.isFinite(aCreatedAt) && Number.isFinite(bCreatedAt) && aCreatedAt !== bCreatedAt) {
    return bCreatedAt - aCreatedAt;
  }

  const aTransactionId = Number(getPaymentTransactionId(a.payment));
  const bTransactionId = Number(getPaymentTransactionId(b.payment));
  if (Number.isFinite(aTransactionId) && Number.isFinite(bTransactionId) && aTransactionId !== bTransactionId) {
    return bTransactionId - aTransactionId;
  }

  return a.index - b.index;
};

const paymentsHaveSameMethodAndAmount = (payment, refund) => {
  if (getPaymentMethodMatchKey(payment) !== getPaymentMethodMatchKey(refund)) return false;
  const paymentAmount = Math.abs(Number(getPaymentAmountValue(payment)) || 0);
  const refundAmount = Math.abs(Number(getPaymentAmountValue(refund)) || 0);
  return Math.abs(paymentAmount - refundAmount) < 0.01;
};

/** Keep newest payments first and place every refund immediately below its payment. */
const sortPaymentsWithRefunds = (payments) => {
  if (!Array.isArray(payments) || payments.length <= 1) return payments;

  const paymentRows = [];
  const refundRows = [];

  payments.forEach((payment, index) => {
    if (isRefundPaymentRow(payment)) {
      refundRows.push({ payment, index });
      return;
    }
    paymentRows.push({
      payment,
      index,
      txnId: getPaymentTransactionId(payment) != null ? String(getPaymentTransactionId(payment)) : null,
    });
  });

  const sorted = [];
  const usedRefundIndexes = new Set();

  paymentRows.sort(comparePaymentsNewestFirst);
  paymentRows.forEach(({ payment, txnId }) => {
    sorted.push(payment);

    const linkedRefundIndexes = [];
    refundRows.forEach((refundEntry, refundIndex) => {
      if (usedRefundIndexes.has(refundIndex)) return;
      const parentTxnId = getParentTransactionId(refundEntry.payment);
      if (parentTxnId && txnId && parentTxnId === txnId) {
        linkedRefundIndexes.push(refundIndex);
      }
    });

    // Some membership refund responses omit parent_transaction_id. In that
    // case, method + amount is the only stable link back to the payment.
    if (linkedRefundIndexes.length === 0) {
      const fallbackRefundIndex = refundRows.findIndex(
        (refundEntry, refundIndex) =>
          !usedRefundIndexes.has(refundIndex) &&
          !getParentTransactionId(refundEntry.payment) &&
          paymentsHaveSameMethodAndAmount(payment, refundEntry.payment)
      );
      if (fallbackRefundIndex !== -1) linkedRefundIndexes.push(fallbackRefundIndex);
    }

    linkedRefundIndexes.forEach((refundIndex) => {
      usedRefundIndexes.add(refundIndex);
      sorted.push(refundRows[refundIndex].payment);
    });
  });

  refundRows
    .filter((_, refundIndex) => !usedRefundIndexes.has(refundIndex))
    .sort(comparePaymentsNewestFirst)
    .forEach(({ payment }) => sorted.push(payment));

  return sorted.length > 0 ? sorted : payments;
};

const getPaymentMethodMatchKey = (payment) => {
  const methodId = payment?.payment_method_id ?? payment?.method_id;
  return methodId != null && String(methodId).trim() !== ''
    ? `id:${methodId}`
    : `name:${String(getPaymentMethodName(payment)).trim().toLowerCase()}`;
};

/**
 * Membership sale / top-up detail for Sales list sidebar (GET /sales/:id/details?type=membership)
 * panelType: "sales" (default) | "membershipCredit" (payment row menu: View Details)
 */
const MembershipSaleSidebarInfo = ({
  detail,
  saleNote,
  showAddNotePopup,
  panelType = 'sales',
  paymentDetails = [],
  onPaymentViewDetails = null,
  onAddPayment = null,
  onOpenRelatedSale = null,
}) => {
  const [openPaymentMenuKey, setOpenPaymentMenuKey] = useState(null);
  const paymentMenuRef = useRef(null);
  // const showPaymentActions = panelType === 'membershipCredit';
  const showPaymentActions = true;

  useEffect(() => {
    if (!showPaymentActions) return undefined;
    const handleClickOutside = (event) => {
      if (paymentMenuRef.current && !paymentMenuRef.current.contains(event.target)) {
        setOpenPaymentMenuKey(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPaymentActions]);

  if (!detail) return null;

  /* Left headline: "Sale" for top-ups; otherwise action / package title */
  const actionLabel = (detail.action_label || '').trim();
  const headlineText = actionLabel === "Membership Refund" ? `${actionLabel}` : `Membership ${actionLabel}`;
  const lineAmount = detail.amount != null ? detail.amount : detail.grand_total;
  const giftItems = Array.isArray(detail.gifts) ? detail.gifts : [];
  const assignedGifts = giftItems.filter(hasAssignedGift);
  const saleNoteText = String(saleNote ?? detail.sale_note ?? '').trim();

  // Check if it's a partial payment
  const normalizedStatus = String(detail?.status || '').trim().toLowerCase();
  const normalizedPaymentStatus = String(detail?.payment_status || '').trim().toLowerCase();
  const normalizedAction = String(detail?.action || '').trim().toLowerCase();
  const normalizedActionLabel = String(detail?.action_label || '').trim().toLowerCase();
  const normalizeStatusKey = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, '');
  const isPartialPayment =normalizeStatusKey(detail?.status) === 'partial' || normalizeStatusKey(detail?.payment_status) === 'partial' || normalizeStatusKey(detail?.status) === 'open(inprogress)' || normalizeStatusKey(detail?.payment_status) === 'open(inprogress)';
  const isRefunded = normalizedStatus === 'refunded' || normalizedPaymentStatus === 'refunded' || normalizedAction === 'refund_membership' || normalizedAction === 'refund' || normalizedActionLabel.includes('refund');
  const refundAmount = detail?.refund_audit?.refund_amount ?? detail?.amount ?? Math.abs(Number(detail?.credit_delta || 0));
  const parentSaleId = detail?.parent_sale_id;
  const refundReason = String(detail?.remarks || detail?.reason || '').trim();
  const detailReason = String(detail?.reason || '').trim();
  const detailRemark = String(detail?.remark || detail?.remarks || '').trim();
  const isOthersReason = detailReason.toLowerCase() === 'others';
  const refundReasonLabel = isOthersReason ? 'Others' : refundReason;
  const refundRemarkText = isOthersReason ? detailRemark : '';
  const hasRelatedRefundSale = Boolean(
    detail?.child_sale_id ||
    detail?.refund_sale_id ||
    detail?.refunded_sale_id ||
    detail?.refunded_transaction_id ||
    detail?.already_refunded ||
    detail?.refund_audit ||
    (Array.isArray(detail?.refund_payments) && detail.refund_payments.length > 0) ||
    (Array.isArray(detail?.refunded_payments) && detail.refunded_payments.length > 0) ||
    (Array.isArray(detail?.child_refund_payments) && detail.child_refund_payments.length > 0) ||
    isRefunded
  );
  const directRefundPayments = [
    ...asArray(detail?.refund_payments),
    ...asArray(detail?.refunded_payments),
    ...asArray(detail?.child_refund_payments),
    ...asArray(detail?.refund_audit?.payments),
    ...asArray(detail?.child_sale?.payments),
  ].filter(hasPaymentMethod);

  const originalPaymentRows = (paymentDetails.length > 0
    ? paymentDetails
    : asArray(detail?.payments)
  ).filter(hasPaymentMethod);
  const hasBackendRefundRows = originalPaymentRows.some(isRefundPaymentRowByCode);
  const originalPaymentTotal = originalPaymentRows.reduce((sum, p) => {
    if (isRefundPaymentRowByCode(p)) return sum;
    const amount = Number(getPaymentAmountValue(p)) || 0;
    return sum + amount;
  }, 0);

  const refundPaymentRows =
    !hasBackendRefundRows && (hasRelatedRefundSale && !isRefunded) && originalPaymentTotal > 0
      ? (
          directRefundPayments.length > 0
            ? directRefundPayments
            : originalPaymentRows.map((payment) => {
                const paymentAmount = Number(getPaymentAmountValue(payment)) || 0;
                const effectiveRefundAmount = Number(refundAmount) > 0 ? Number(refundAmount) : originalPaymentTotal;
                const proportionalRefundAmount = (paymentAmount / originalPaymentTotal) * effectiveRefundAmount;
                return {
                  ...payment,
                  amount_value: proportionalRefundAmount,
                  amount: proportionalRefundAmount,
                };
              })
        ).map((payment, index) => ({
          ...payment,
          __isRefundRow: true,
          __refundRowIndex: index,
        }))
      : [];
  const paymentRows = (() => {
    let rows = hasBackendRefundRows
      ? originalPaymentRows.map((payment) =>
          isRefundPaymentRowByCode(payment) ? { ...payment, __isRefundRow: true } : payment
        )
      : [...originalPaymentRows, ...refundPaymentRows];

    if (isRefunded && hasBackendRefundRows) {
      const refundOnly = rows.filter(isRefundPaymentRow);
      if (refundOnly.length > 0) rows = refundOnly;
    }

    return sortPaymentsWithRefunds(rows);
  })();

  return (
    <>
    <div className={`${styles.infoWrapper} ${ms.sidebarRoot}`} style={{ paddingTop: 0 }}>
      <div className={ms.sidebarMain}>
      {parentSaleId && (
        <div className={styles.refundSaleInfoHeader}>
          This refund is for{" "}
          <button
            type="button"
            className={styles.refundSaleInfoHeaderButton}
            onClick={() => onOpenRelatedSale?.(parentSaleId)}
          >
            Sale #{parentSaleId}
          </button>
        </div>
      )}

      <div className={styles.customerInfoWrapper}>
        <div className={styles.customerCard}>
          <div className={styles.avatarWrapper}>
            <div
              className={styles.avatar}
              style={{
                backgroundColor: getConsistentColor(detail.user_id, detail.user_name),
              }}
            >
              <span>{getInitials(detail.user_name)}</span>
              {detail.membership === 1 && (
              <span className={styles.membershipIcon}>  
                <PrimiumMemberIcon />
              </span>)}
            </div>
          </div>
          <div
            className={styles.customerDetails}
            onClick={() => {
              const contactNumber = detail.user_phone;
              if (contactNumber || detail.user_id) {
                window.dispatchEvent(
                  new CustomEvent('openCustomerDetails', {
                    detail: {
                      customer: {
                        id: detail.user_id,
                        name: detail.user_name,
                        contact_number: contactNumber,
                      },
                    },
                  })
                );
              }
            }}
            style={{
              cursor: detail.user_phone || detail.user_id ? 'pointer' : 'default',
            }}
          >
            <div className={ms.customerTextStack}>
              <p className={ms.phoneLine}>{displayPhone(detail.user_phone)}</p>
              <p className={ms.nameLine}>{detail.user_name || '—'}</p>
              {detail.membership_number != null && (
                <p className={ms.idLine}>(#{detail.membership_number})</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {isRefunded && refundReason ? (
        <SaleCancelReason
          reason={refundReasonLabel}
          remark={refundRemarkText}
        />
      ) : null}
  {saleNoteText ? (
  <div className={ms.notesSection}>
    <SaleNote saleNote={saleNoteText} showAddNotePopup={showAddNotePopup} />
  </div>
) : null}
      {isRefunded ? (
        <div className={ms.refundLineSection}>
          <div className={ms.refundLineHeader}>
            <span>Refund Amount (Membership Top-up)</span>
            <strong>${formatMoney(refundAmount)}</strong>
          </div>
          {assignedGifts.map((g, index) => {
            const serialRaw =
              g.voucher_serial_number != null ? String(g.voucher_serial_number).trim() : '';
            const hasSerial = serialRaw.length > 0;
            const giftLabel =
              (g.gift_category_title || '').trim() ||
              `Gift ${String.fromCharCode(65 + index)}`;
            const returnStatus = getGiftReturnStatus(g);

            return (
              <div key={g.id ?? `refund-gift-${index}`} className={ms.refundGiftLine}>
                <span className={ms.refundGiftLabel}>{giftLabel}</span>
                <span className={ms.refundGiftName}>
                  {giftLabelText(g) || '—'}
                  {returnStatus ? <em> ({returnStatus})</em> : null}
                </span>
                {hasSerial ? <span className={ms.refundGiftSerial}>{serialRaw}</span> : null}
              </div>
            );
          })}
        </div>
      ) : null}


      {!isRefunded ? (
        <div className={ms.giftsSection}>
          {/* <h3 className={ms.giftsHeading}>Gift Options</h3> */}
          <div className={ms.txHeadRow}>
          <div className={ms.txTitle}>{headlineText}</div>
          <span className={ms.txHeadAmount}>{formatHeadlineAmount(lineAmount)}</span>
        </div>
          {assignedGifts.map((g, index) => {
            const serialRaw =
              g.voucher_serial_number != null ? String(g.voucher_serial_number).trim() : '';
            const hasSerial = serialRaw.length > 0;
            const giftLabel =
              (g.gift_category_title || '').trim() ||
              `Gift ${String.fromCharCode(65 + index)}`;

            return (
              <div key={g.id ?? `gift-${index}`} className={ms.giftCard}>
                <span className={ms.giftRowLabel}>{giftLabel}</span>
                <span className={ms.giftRowColon} aria-hidden />
                <div className={ms.giftRowValueCell}>
                  <span className={ms.giftRowValue}>{giftLabelText(g) || '—'}</span>
                  {hasSerial ? (
                    <span className={ms.giftSerialValue}>{serialRaw}</span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      <div className={`${ms.financialBlock} ${isRefunded ? ms.refundFinancialBlock : ''}`}>
        {isRefunded ? (
          <>
            <div className={`${ms.totalRow} ${ms.refundAmountPrimaryRow}`}>
              <span>Refund Amount</span>
              <span>${formatMoney(refundAmount)}</span>
            </div>
            <div className={`${ms.totalRow} ${ms.refundSummaryRow}`}>
              <span>Subtotal</span>
              <span>${formatMoney(refundAmount)}</span>
            </div>
            <div className={`${ms.totalRow} ${ms.refundTotalSummaryRow} `}>
              <span>Total Refund</span>
              <span>${formatMoney(refundAmount)}</span>
            </div>
          </>
        ) : (
          <div className={ms.totalRow}>
            <span>Total</span>
            <span>${formatMoney(detail.grand_total)}</span>
          </div>
        )}
        {paymentRows.length > 0 ? (
          <>
            <p className={ms.transactionsHeading}></p>
            <div ref={showPaymentActions ? paymentMenuRef : null}>
            {paymentRows.map((payment, index) => {
              const paymentMethod = getPaymentMethodName(payment);
              if (!paymentMethod) {
                return null;
              }
              const isRefundRow = isRefundPaymentRow(payment);
              const paymentAmount = getPaymentAmountValue(payment);
              const baseKey = getPaymentTransactionId(payment) ?? payment.id ?? `${paymentMethod}-${paymentAmount}-${index}`;
              const key = `${baseKey}-${isRefundRow ? 'refund' : 'paid'}`;
              let methodLabel = paymentMethod;
              if (isRefundRow) {
                methodLabel = `${paymentMethod} (Refund)`;
              } else if (isRefunded) {
                methodLabel = `Refund (${paymentMethod})`;
              }
              return (
                <div key={key} className={ms.paymentRow} style={showPaymentActions ? { position: 'relative' } : undefined}>
                  <span className={ms.paymentMethod}>
                    {methodLabel}
                    {payment.original_reference && !isRefundRow ? (
                      <div className={ms.referenceText}>
                        <i>{payment.original_reference}</i>
                      </div>
                    ) : null}
                   
                   
                  </span>
                  <span className={ms.paymentRowSpacer} aria-hidden />
                  {showPaymentActions ? (
                    <button
                      type="button"
                      className={ms.paymentDotsButton}
                      aria-label="Payment options"
                      onClick={(event) => {
                        event.stopPropagation();
                        setOpenPaymentMenuKey((prev) => (prev === key ? null : key));
                      }}
                    >
                      <img src={menuIcon} alt="" className={ms.paymentMenuIcon} />
                    </button>
                  ) : null}
                  <span className={ms.paymentAmount}>
                    {(isRefunded || isRefundRow) ? '-' : ''}${formatMoney((isRefunded || isRefundRow) ? Math.abs(Number(paymentAmount || 0)) : paymentAmount)}
                  </span>
                  {showPaymentActions && openPaymentMenuKey === key ? (
                    <ul className={ms.paymentMenuDropdown}>
                      <li
                        className={ms.paymentMenuItem}
                        onClick={() => {
                          setOpenPaymentMenuKey(null);
                          if (typeof onPaymentViewDetails === 'function') {
                            onPaymentViewDetails(payment);
                          }
                        }}
                      >
                        View Details
                      </li>
                    </ul>
                  ) : null}
                </div>
              );
            })}
            </div>
          </>
        ) : null}
      </div>



      {!isPartialPayment && (
        <div className={`${ms.saleDetailsWrap} ${typeof onAddPayment === 'function' ? ms.saleDetailsWithPayment : ''}`}>
          <BookingDetails
            heading={isRefunded ? "Refund Details" : "Sale Details"}
            CreateDate={detail.created_at}
            CreatedBy={detail.created_by_name}
            UpdatedDate={detail.updated_at}
            LastUpdatedBy={detail.updated_by_name || "null" }
            BookingDate={detail.booking_date}
          />
        </div>
      )}

      </div>
    </div>
    {typeof onAddPayment === 'function' && (
      <div className={ms.addPaymentWrap}>
        <button type="button" className={ms.addPaymentButton} onClick={onAddPayment}>
          Go to Payments
        </button>
      </div>
    )}
    </>
  );
};

export default MembershipSaleSidebarInfo;
